// ═══════════════════════════════════════════════
//   KONBINI STACK — minigame (Suika-style)
// ═══════════════════════════════════════════════
//
// Objective:   Drop konbini items into the bin; two of the same tier
//              touching merge into the next tier. Don't let the stack
//              overflow the danger line.
// Type:        physics-merge
// Description: Mouse/touch X positions a drop preview at the top of the
//              bin; click or tap to release. Same-tier circles in contact
//              merge into the next tier (8 tiers — candy → ramen). Round
//              ends when a settled circle sits above the danger line for
//              dangerGraceMs continuous milliseconds. End-of-run reward
//              scales with the score.
// Tunables:    GAME_SUIKA in data.js.

const {S, canvas, ctx, claimGameReward, addCleanup, showGameOver} = window;

// ── Image preload ────────────────────────────────────────────────────
// Load each tier's PNG once at module load. While the file is missing,
// the renderer falls back to the emoji + a colored circle so the game
// works with placeholders.
(function preloadSuika() {
    const tiers = window.GAME_SUIKA && window.GAME_SUIKA.tiers;
    if (!tiers) return;
    for (const t of tiers) {
        if (!t.img) continue;
        const im = new Image();
        im.src = t.img;
        t._img = im;
    }
})();

// ── Local runtime state ──────────────────────────────────────────────
window.suikaCircles    = [];
window.suikaPreviewX   = 0;
window.suikaPreviewT   = 1;
window.suikaNextT      = 1;
window.suikaLastDropAt = 0;
window.suikaDangerSince = 0; // ms timestamp when danger started, 0 = safe
window.suikaFlashes    = []; // expanding-ring effects for top-tier explosions

function suikaSpawnTier() {
    const CFG = window.GAME_SUIKA;
    return 1 + Math.floor(Math.random() * CFG.spawnTierMax);
}

function startSuika() {
    const CFG = window.GAME_SUIKA;
    window.gameRunning = true;
    window.gameScore = 0;
    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = ''; // no timer for this game

    window.suikaCircles    = [];
    window.suikaPreviewX   = canvas.width / 2;
    window.suikaPreviewT   = suikaSpawnTier();
    window.suikaNextT      = suikaSpawnTier();
    window.suikaLastDropAt = 0;
    window.suikaDangerSince = 0;
    window.suikaFlashes    = [];

    window.gameInterval = setInterval(suikaLoop, 16);
    addCleanup(() => clearInterval(window.gameInterval));

    canvas.addEventListener('mousemove', suikaPointerMove);
    canvas.addEventListener('touchmove', suikaPointerMove, {passive: false});
    canvas.addEventListener('click', suikaPointerDown);
    canvas.addEventListener('touchstart', suikaPointerDown, {passive: false});
    addCleanup(() => canvas.removeEventListener('mousemove', suikaPointerMove));
    addCleanup(() => canvas.removeEventListener('touchmove', suikaPointerMove));
    addCleanup(() => canvas.removeEventListener('click', suikaPointerDown));
    addCleanup(() => canvas.removeEventListener('touchstart', suikaPointerDown));

    addCleanup(() => { window.suikaCircles = []; window.suikaFlashes = []; });
}

function suikaPointerMove(e) {
    if (!window.gameRunning || S.currentGame !== 'suika') return;
    if (e.cancelable && e.type === 'touchmove') e.preventDefault();
    window.suikaPreviewX = canvasPos(e).x;
}

function suikaPointerDown(e) {
    if (!window.gameRunning || S.currentGame !== 'suika') return;
    if (e.cancelable && e.type === 'touchstart') e.preventDefault();
    const CFG = window.GAME_SUIKA;
    const now = Date.now();
    if (now - window.suikaLastDropAt < CFG.dropCooldownMs) return;
    if (e.type === 'touchstart') window.suikaPreviewX = canvasPos(e).x;

    const tier = window.suikaPreviewT;
    const meta = CFG.tiers[tier - 1];
    const r = meta.r;
    const x = Math.max(CFG.binLeft + r, Math.min(CFG.binRight - r, window.suikaPreviewX));
    window.suikaCircles.push({
        x, y: CFG.dropY, vx: 0, vy: 0, tier, r,
        inBin: false, justMergedFrames: 0,
    });
    SFX.play('click');
    window.suikaLastDropAt = now;
    window.suikaPreviewT = window.suikaNextT;
    window.suikaNextT    = suikaSpawnTier();
}

// ── Physics step ─────────────────────────────────────────────────────
function suikaPhysics() {
    const CFG = window.GAME_SUIKA;
    const arr = window.suikaCircles;

    // Integrate
    for (const c of arr) {
        c.vy += CFG.gravity;
        c.vx *= CFG.damping;
        c.vy *= CFG.damping;
        if (c.vx >  CFG.maxSpeed) c.vx =  CFG.maxSpeed;
        if (c.vx < -CFG.maxSpeed) c.vx = -CFG.maxSpeed;
        if (c.vy >  CFG.maxSpeed) c.vy =  CFG.maxSpeed;
        if (c.vy < -CFG.maxSpeed) c.vy = -CFG.maxSpeed;
        c.x += c.vx;
        c.y += c.vy;
        if (c.justMergedFrames > 0) c.justMergedFrames--;
    }

    // Walls + floor
    for (const c of arr) {
        if (c.x - c.r < CFG.binLeft) {
            c.x = CFG.binLeft + c.r;
            c.vx = -c.vx * CFG.restitution * CFG.wallFriction;
        }
        if (c.x + c.r > CFG.binRight) {
            c.x = CFG.binRight - c.r;
            c.vx = -c.vx * CFG.restitution * CFG.wallFriction;
        }
        if (c.y + c.r > CFG.binBottom) {
            c.y = CFG.binBottom - c.r;
            c.vy = -c.vy * CFG.restitution;
            c.vx *= CFG.floorFriction;
        }
        if (!c.inBin && c.y - c.r > CFG.dangerLineY) c.inBin = true;
    }

    // Pairwise — collisions + merges (one merge per pair per frame, both
    // freshly-merged circles get justMergedFrames so they can't immediately
    // re-merge in the same physics tick).
    const merges = [];
    for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
            const a = arr[i], b = arr[j];
            const dx = b.x - a.x, dy = b.y - a.y;
            const distSq = dx * dx + dy * dy;
            const minD = a.r + b.r;
            if (distSq >= minD * minD) continue;
            const dist = Math.sqrt(distSq) || 0.0001;
            const nx = dx / dist, ny = dy / dist;
            // Merge? Same-tier circles always merge — when newTier exceeds the
            // ladder length the merge becomes an explosion (handled below).
            if (a.tier === b.tier
                && a.justMergedFrames === 0 && b.justMergedFrames === 0) {
                merges.push({i, j});
                continue;
            }
            // Otherwise, separate + impulse
            const overlap = minD - dist;
            a.x -= nx * overlap * 0.5;
            a.y -= ny * overlap * 0.5;
            b.x += nx * overlap * 0.5;
            b.y += ny * overlap * 0.5;
            const rvx = b.vx - a.vx, rvy = b.vy - a.vy;
            const velAlongNormal = rvx * nx + rvy * ny;
            if (velAlongNormal > 0) continue;
            const impulse = -(1 + CFG.restitution) * velAlongNormal / 2;
            a.vx -= impulse * nx; a.vy -= impulse * ny;
            b.vx += impulse * nx; b.vy += impulse * ny;
        }
    }

    // Apply merges (highest indices first so splice doesn't shift them).
    if (merges.length) {
        const used = new Set();
        const sorted = merges.sort((a, b) => Math.max(b.i, b.j) - Math.max(a.i, a.j));
        for (const m of sorted) {
            if (used.has(m.i) || used.has(m.j)) continue;
            used.add(m.i); used.add(m.j);
            const a = arr[m.i], b = arr[m.j];
            const newTier = a.tier + 1;
            const isExplosion = newTier > CFG.tiers.length;
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;
            const hi = Math.max(m.i, m.j), lo = Math.min(m.i, m.j);
            arr.splice(hi, 1);
            arr.splice(lo, 1);

            if (isExplosion) {
                // Two top-tier ramens fused — both vanish, big bonus, flash effect.
                window.gameScore += CFG.explosionBonus;
                SFX.play('win');
                window.suikaFlashes.push({
                    x: midX, y: midY, age: 0, maxAge: 22,
                    baseR: CFG.tiers[CFG.tiers.length - 1].r,
                });
            } else {
                const meta = CFG.tiers[newTier - 1];
                arr.push({
                    x: midX, y: midY,
                    vx: (a.vx + b.vx) / 2,
                    vy: (a.vy + b.vy) / 2,
                    tier: newTier,
                    r: meta.r,
                    inBin: a.inBin || b.inBin,
                    justMergedFrames: 4,
                });
                window.gameScore += newTier * CFG.mergePointMul;
                if (newTier === CFG.tiers.length) window.gameScore += CFG.topTierBonus;
                SFX.play(newTier === CFG.tiers.length ? 'win' : 'pts');
            }
            document.getElementById('mg-score').textContent = window.gameScore;
        }
    }

    // Danger check — any circle that has been inBin and now breaches the line
    const now = Date.now();
    let danger = false;
    for (const c of arr) {
        if (c.inBin && (c.y - c.r) < CFG.dangerLineY) { danger = true; break; }
    }
    if (danger) {
        if (!window.suikaDangerSince) window.suikaDangerSince = now;
        else if (now - window.suikaDangerSince > CFG.dangerGraceMs) endSuika();
    } else {
        window.suikaDangerSince = 0;
    }
}

// ── Render ───────────────────────────────────────────────────────────
function suikaRender() {
    const CFG = window.GAME_SUIKA;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Background
    ctx.fillStyle = '#1a0010';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Bin walls + floor
    ctx.strokeStyle = '#f72585';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(CFG.binLeft, CFG.dropY + 10);
    ctx.lineTo(CFG.binLeft, CFG.binBottom);
    ctx.lineTo(CFG.binRight, CFG.binBottom);
    ctx.lineTo(CFG.binRight, CFG.dropY + 10);
    ctx.stroke();

    // Danger line
    const dangerActive = window.suikaDangerSince > 0;
    ctx.strokeStyle = dangerActive ? '#ef4444' : 'rgba(255, 99, 132, 0.5)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(CFG.binLeft, CFG.dangerLineY);
    ctx.lineTo(CFG.binRight, CFG.dangerLineY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Circles
    for (const c of window.suikaCircles) drawSuikaCircle(c);

    // Explosion flashes (expanding ring + fading core)
    const flashes = window.suikaFlashes;
    for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        f.age++;
        if (f.age > f.maxAge) { flashes.splice(i, 1); continue; }
        const t = f.age / f.maxAge;
        const ringR = f.baseR * (1 + t * 1.6);
        ctx.globalAlpha = 1 - t;
        ctx.fillStyle = '#fde047';
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.baseR * (1 - t * 0.6), 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff7ae';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(f.x, f.y, ringR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
    }

    // Drop preview
    const previewX = Math.max(CFG.binLeft + CFG.tiers[window.suikaPreviewT - 1].r,
        Math.min(CFG.binRight - CFG.tiers[window.suikaPreviewT - 1].r, window.suikaPreviewX));
    drawSuikaCircle({ x: previewX, y: CFG.dropY, tier: window.suikaPreviewT, r: CFG.tiers[window.suikaPreviewT - 1].r }, 0.6);

    // "Next" indicator (top-right)
    const nextMeta = CFG.tiers[window.suikaNextT - 1];
    ctx.font = '10px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.textAlign = 'right';
    ctx.fillText('next', canvas.width - 6, 12);
    drawSuikaCircle({ x: canvas.width - 14, y: 22, tier: window.suikaNextT, r: Math.min(nextMeta.r, 12) }, 0.9);
}

function drawSuikaCircle(c, alpha = 1) {
    const CFG = window.GAME_SUIKA;
    const tier = CFG.tiers[c.tier - 1];
    if (alpha < 1) ctx.globalAlpha = alpha;
    if (tier._img && tier._img.complete && tier._img.naturalWidth) {
        ctx.drawImage(tier._img, c.x - c.r, c.y - c.r, c.r * 2, c.r * 2);
    } else {
        ctx.fillStyle = tier.bg;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.25)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.font = `${Math.floor(c.r * 1.5)}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tier.emoji, c.x, c.y + 1);
    }
    if (alpha < 1) ctx.globalAlpha = 1;
}

function suikaLoop() {
    if (window.gameRunning) suikaPhysics();
    suikaRender();
}

// ── End of round ─────────────────────────────────────────────────────
function endSuika() {
    const CFG = window.GAME_SUIKA;
    if (!window.gameRunning) return;
    window.gameRunning = false;
    SFX.play(window.gameScore > 0 ? 'win' : 'gameover');
    if (window.gameScore >= CFG.nickThreshold) window.unlockNick('chef');
    const p = Math.min(CFG.rewardMax, Math.floor(window.gameScore / CFG.rewardDivisor));
    showGameOver(claimGameReward('suika', window.gameScore, p));
}

window.startSuika        = startSuika;
window.endSuika          = endSuika;
window.suikaLoop         = suikaLoop;
window.suikaPointerMove  = suikaPointerMove;
window.suikaPointerDown  = suikaPointerDown;
